import { useId } from 'react';

import { Controller, useFormContext } from 'react-hook-form';

import { FieldError } from '@nesso/ui/primitives/field';
import { RadioGroup } from '@nesso/ui/primitives/radio-group';

import { ProfileFormSection } from './profile-form-section';
import { RadioButtonItem } from './radio-button-item';

type ContactPreferencesFormValues = {
    okToEmail: boolean | null;
    okToCallText: boolean | null;
};

type BooleanChoiceFieldName = keyof ContactPreferencesFormValues;

function BooleanChoiceField({
    sectionTitleId,
    label,
    name,
    trueLabel,
    falseLabel,
}: {
    sectionTitleId: string;
    label: string;
    name: BooleanChoiceFieldName;
    trueLabel: string;
    falseLabel: string;
}) {
    const {
        control,
        trigger,
        formState: { isSubmitting },
    } = useFormContext<ContactPreferencesFormValues>();
    const labelId = useId();
    const errorId = useId();

    // `''` rather than `undefined` for "nothing selected": `undefined` makes Radix treat the group as
    // uncontrolled, and it flips to controlled on the first pick. No item has an empty value, so
    // `''` matches none.
    // A choice is complete the moment it is made, so it validates on selection (FR-034), sooner
    // than blur — which is when FR-033's cross-field rule surfaces.
    return (
        <Controller
            control={control}
            name={name}
            render={({ field, fieldState }) => (
                <div className="flex flex-col gap-3">
                    <p id={labelId} className="text-base">
                        {label}
                    </p>
                    <RadioGroup
                        aria-labelledby={`${sectionTitleId} ${labelId}`}
                        aria-describedby={fieldState.error ? errorId : undefined}
                        value={
                            field.value === null || field.value === undefined
                                ? ''
                                : String(field.value)
                        }
                        onValueChange={(value) => {
                            field.onChange(value === 'true');
                            void trigger(name);
                        }}
                        disabled={isSubmitting}
                        className="grid grid-cols-[12.5rem] gap-6 md:gap-3 md:grid-cols-[repeat(2,12.5rem)]"
                    >
                        <RadioButtonItem value="true" title={trueLabel} size="sm" />
                        <RadioButtonItem value="false" title={falseLabel} size="sm" />
                    </RadioGroup>
                    <FieldError id={errorId}>{fieldState.error?.message}</FieldError>
                </div>
            )}
        />
    );
}

// The section comes with the fields because each group's accessible name carries the section
// heading ("Contact preferences Email"), which is what tells the two Email groups on a form apart.
export function ContactPreferencesSection() {
    const contactTitleId = useId();

    return (
        <ProfileFormSection title="Contact preferences" titleId={contactTitleId}>
            <BooleanChoiceField
                sectionTitleId={contactTitleId}
                label="Email"
                name="okToEmail"
                trueLabel="Ok to email"
                falseLabel="Do NOT email"
            />
            <BooleanChoiceField
                sectionTitleId={contactTitleId}
                label="Call or text"
                name="okToCallText"
                trueLabel="Ok to call or text"
                falseLabel="Do NOT call or text"
            />
        </ProfileFormSection>
    );
}
